import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Card, CardContent, CardMedia, Button,
  LinearProgress, Box, Chip, Alert, Divider, CardActions, Avatar, Rating,
} from '@mui/material';
import {
  PlayCircleOutline, CheckCircle, Schedule, Star, School,
  TrendingUp, EmojiEvents, BookmarkAdded,
} from '@mui/icons-material';
import SimpleGrid from '../components/SimpleGrid';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { learnerAPI } from '../services/api';
import { FullCourse } from '../types';

// ── localStorage helpers (mirrors CourseDetailPage) ────────────────────────

interface EnrolledEntry  { courseTitle: string; startedAt: string; }
interface CertEntry      { percentage: number; date: string; }

const readAllEnrolled = (): Record<string, EnrolledEntry> => {
  const out: Record<string, EnrolledEntry> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key?.startsWith('sedstart_enrolled_')) continue;
    try {
      const courseId = key.replace('sedstart_enrolled_', '');
      out[courseId] = JSON.parse(localStorage.getItem(key)!);
    } catch {}
  }
  return out;
};

const readAllCerts = (): Record<string, CertEntry> => {
  const out: Record<string, CertEntry> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key?.startsWith('sedstart_cert_')) continue;
    try {
      const courseId = key.replace('sedstart_cert_', '');
      out[courseId] = JSON.parse(localStorage.getItem(key)!);
    } catch {}
  }
  return out;
};

// ── Component ──────────────────────────────────────────────────────────────

const LearnerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user }  = useAuth();

  const [courses, setCourses]   = useState<FullCourse[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  // Derived from localStorage — refreshed on every render
  const enrolled = readAllEnrolled();
  const certs    = readAllCerts();

  useEffect(() => {
    if (!user?.profile_completed) { navigate('/profile'); return; }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      const res = await learnerAPI.getCourses();
      setCourses(res.data.courses || []);
    } catch {
      setError('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleCourseClick = (courseId: string | number) => navigate(`/courses/${courseId}`);

  if (loading) return <Container><Box sx={{ mt: 4 }}><LinearProgress /></Box></Container>;

  // ── Partition courses ───────────────────────────────────────────────────

  const completedCourses   = courses.filter(c => !!certs[c.id]);
  const inProgressCourses  = courses.filter(c => !!enrolled[c.id] && !certs[c.id]);
  const exploreCourses     = courses.filter(c => !enrolled[c.id] && !certs[c.id]);

  const totalEnrolled   = inProgressCourses.length + completedCourses.length;
  const hasActivity     = totalEnrolled > 0;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 52, height: 52 }}>
          <School />
        </Avatar>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 0 }}>
            Welcome back, {user?.name}!
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Continue your learning journey
          </Typography>
        </Box>
      </Box>

      {/* Stats */}
      {hasActivity && (
        <SimpleGrid container spacing={3} sx={{ mb: 5 }}>
          {[
            { icon: <TrendingUp sx={{ fontSize: 38 }} />, color: 'primary.main',
              value: totalEnrolled, label: 'Enrolled' },
            { icon: <PlayCircleOutline sx={{ fontSize: 38 }} />, color: 'warning.main',
              value: inProgressCourses.length, label: 'In Progress' },
            { icon: <CheckCircle sx={{ fontSize: 38 }} />, color: 'success.main',
              value: completedCourses.length, label: 'Completed' },
            { icon: <Star sx={{ fontSize: 38 }} />, color: 'info.main',
              value: courses.length, label: 'Available' },
          ].map(({ icon, color, value, label }) => (
            <SimpleGrid item xs={6} sm={3} key={label}>
              <Card sx={{ textAlign: 'center', py: 1 }}>
                <CardContent>
                  <Box sx={{ color }}>{icon}</Box>
                  <Typography variant="h4" sx={{ color, fontWeight: 700 }}>{value}</Typography>
                  <Typography variant="body2" color="text.secondary">{label}</Typography>
                </CardContent>
              </Card>
            </SimpleGrid>
          ))}
        </SimpleGrid>
      )}

      {/* ── Continue Learning ─────────────────────────────────────────── */}
      {inProgressCourses.length > 0 && (
        <Box sx={{ mb: 5 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2.5}>
            <PlayCircleOutline color="primary" />
            <Typography variant="h5" fontWeight={700}>Continue Learning</Typography>
            <Chip label={inProgressCourses.length} size="small" color="primary" />
          </Box>

          <SimpleGrid container spacing={3}>
            {inProgressCourses.map(course => (
              <SimpleGrid item xs={12} sm={6} md={4} key={course.id}>
                <Card sx={{
                  height: '100%', display: 'flex', flexDirection: 'column',
                  border: '2px solid', borderColor: 'primary.main',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
                }}>
                  {course.thumbnail && (
                    <CardMedia component="img" height="150" image={course.thumbnail} alt={course.title} />
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="h6" fontWeight={600} sx={{ flex: 1, pr: 1 }}>
                        {course.title}
                      </Typography>
                      <Chip label="In Progress" color="primary" size="small" />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {course.description}
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                      <Chip label={course.difficulty} size="small" variant="outlined" />
                      <Chip label={course.category} size="small" variant="outlined" />
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Schedule sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        Started {new Date(enrolled[course.id]?.startedAt || '').toLocaleDateString()}
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button fullWidth variant="contained" startIcon={<PlayCircleOutline />}
                      onClick={() => handleCourseClick(course.id)}>
                      Continue Learning
                    </Button>
                  </CardActions>
                </Card>
              </SimpleGrid>
            ))}
          </SimpleGrid>
        </Box>
      )}

      {/* ── Completed ─────────────────────────────────────────────────── */}
      {completedCourses.length > 0 && (
        <Box sx={{ mb: 5 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2.5}>
            <CheckCircle color="success" />
            <Typography variant="h5" fontWeight={700}>Completed</Typography>
            <Chip label={completedCourses.length} size="small" color="success" />
          </Box>

          <SimpleGrid container spacing={3}>
            {completedCourses.map(course => (
              <SimpleGrid item xs={12} sm={6} md={4} key={course.id}>
                <Card sx={{
                  height: '100%', display: 'flex', flexDirection: 'column',
                  border: '2px solid', borderColor: 'success.main',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
                }}>
                  {course.thumbnail && (
                    <CardMedia component="img" height="150" image={course.thumbnail} alt={course.title} />
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="h6" fontWeight={600} sx={{ flex: 1, pr: 1 }}>
                        {course.title}
                      </Typography>
                      <Chip icon={<CheckCircle />} label="Completed" color="success" size="small" />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {course.description}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                      <EmojiEvents sx={{ fontSize: 14, color: 'warning.main' }} />
                      <Typography variant="caption" color="text.secondary">
                        Completed on {certs[course.id]?.date}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Star sx={{ fontSize: 14, color: 'gold' }} />
                      <Rating value={5} size="small" readOnly max={5} />
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0, flexDirection: 'column', gap: 1 }}>
                    <Button fullWidth variant="outlined" color="success" startIcon={<BookmarkAdded />}
                      onClick={() => handleCourseClick(course.id)}>
                      View Certificate
                    </Button>
                    <Button fullWidth variant="text" size="small"
                      onClick={() => handleCourseClick(course.id)}>
                      Review Course
                    </Button>
                  </CardActions>
                </Card>
              </SimpleGrid>
            ))}
          </SimpleGrid>
        </Box>
      )}

      {/* ── Divider + Explore ─────────────────────────────────────────── */}
      {hasActivity && <Divider sx={{ mb: 5 }} />}

      <Box display="flex" alignItems="center" gap={1} mb={2.5}>
        <School color="action" />
        <Typography variant="h5" fontWeight={700}>
          {exploreCourses.length > 0 ? 'Explore Courses' : 'All Courses'}
        </Typography>
        {exploreCourses.length > 0 && (
          <Chip label={exploreCourses.length} size="small" variant="outlined" />
        )}
      </Box>

      {exploreCourses.length > 0 ? (
        <SimpleGrid container spacing={3}>
          {exploreCourses.map(course => (
            <SimpleGrid item xs={12} sm={6} md={4} key={course.id}>
              <Card sx={{
                height: '100%', display: 'flex', flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
              }}>
                {course.thumbnail && (
                  <CardMedia component="img" height="150" image={course.thumbnail} alt={course.title} />
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {course.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary"
                    sx={{ mb: 2, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {course.description}
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                    <Chip label={course.difficulty} size="small" variant="outlined" />
                    <Chip label={course.category} size="small" variant="outlined" />
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Schedule sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {course.duration} min
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Star sx={{ fontSize: 14, color: 'gold' }} />
                      <Typography variant="caption">4.5</Typography>
                    </Box>
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button fullWidth variant="contained" startIcon={<School />}
                    onClick={() => handleCourseClick(course.id)}>
                    Enroll Now
                  </Button>
                </CardActions>
              </Card>
            </SimpleGrid>
          ))}
        </SimpleGrid>
      ) : (
        <Alert severity="success" icon={<EmojiEvents />}>
          You've enrolled in all available courses. Keep up the great work!
        </Alert>
      )}

      {courses.length === 0 && (
        <Box textAlign="center" mt={6}>
          <School sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No courses available yet</Typography>
          <Typography variant="body2" color="text.secondary">Check back later for new learning opportunities!</Typography>
        </Box>
      )}
    </Container>
  );
};

export default LearnerDashboard;
